import { eventBus } from '../core/EventBus';
import { Plant } from '../plants/Plant';

const stateNames: Record<string, string> = {
  thriving: '蓬勃',
  lazy: '慵懒',
  withering: '枯萎',
  dormant: '休眠'
};

const stateColors: Record<string, string> = {
  thriving: '#4a6b3a',
  lazy: '#b89830',
  withering: '#b06038',
  dormant: '#7a6a5a'
};

export class PlantInfoPanel {
  private el: HTMLDivElement;
  private plant: Plant;
  private unsubUpdate: () => void;
  private onDestroy: () => void;

  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private panelStartX: number = 0;
  private panelStartY: number = 0;
  private dragging: boolean = false;
  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnMouseUp: (e: MouseEvent) => void;

  private plantNameEl!: HTMLSpanElement;
  private stageEl!: HTMLSpanElement;
  private stateEl!: HTMLDivElement;
  private stateLabelEl!: HTMLSpanElement;
  private healthFill!: HTMLDivElement;
  private healthLabel!: HTMLSpanElement;
  private gaugeRows!: HTMLDivElement[];
  private adviceEl!: HTMLDivElement;

  constructor(plant: Plant, uiLayer: HTMLElement, onDestroy: () => void) {
    this.plant = plant;
    this.onDestroy = onDestroy;

    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnMouseUp = this.onMouseUp.bind(this);

    this.el = document.createElement('div');
    this.el.className = 'plant-info-panel';
    this.buildDOM();
    uiLayer.appendChild(this.el);

    this.unsubUpdate = eventBus.on('game:update', () => this.refresh());
  }

  private buildDOM(): void {
    this.el.innerHTML = `
      <div class="panel-header">
        <span class="panel-plant-name"></span>
        <span class="panel-stage"></span>
        <button class="panel-close">&times;</button>
      </div>
      <div class="panel-state">
        <span class="state-dot"></span>
        <span class="state-label"></span>
        <span class="health-label-inline"></span>
      </div>
      <div class="health-bar">
        <div class="health-fill"></div>
      </div>
      <div class="panel-gauges">
        <div class="gauge-row">
          <span class="gauge-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#b89830;"><circle cx="12" cy="12" r="5"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>
          </span>
          <span class="gauge-name">光照</span>
          <div class="gauge-track"><div class="gauge-fill"></div></div>
          <span class="gauge-value"></span>
          <span class="gauge-status"></span>
        </div>
        <div class="gauge-row">
          <span class="gauge-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#4a6b3a;"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0L12 2.69z"/></svg>
          </span>
          <span class="gauge-name">水分</span>
          <div class="gauge-track"><div class="gauge-fill"></div></div>
          <span class="gauge-value"></span>
          <span class="gauge-status"></span>
        </div>
        <div class="gauge-row">
          <span class="gauge-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="color:#b06038;"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
          </span>
          <span class="gauge-name">温度</span>
          <div class="gauge-track"><div class="gauge-fill"></div></div>
          <span class="gauge-value"></span>
          <span class="gauge-status"></span>
        </div>
      </div>
      <div class="panel-advice"></div>
    `;

    this.plantNameEl = this.el.querySelector('.panel-plant-name')!;
    this.stageEl = this.el.querySelector('.panel-stage')!;
    this.stateEl = this.el.querySelector('.panel-state')!;
    this.stateLabelEl = this.el.querySelector('.state-label')!;
    this.healthFill = this.el.querySelector('.health-fill')!;
    this.healthLabel = this.el.querySelector('.health-label-inline')!;
    this.gaugeRows = Array.from(this.el.querySelectorAll('.gauge-row'));
    this.adviceEl = this.el.querySelector('.panel-advice')!;

    const closeBtn = this.el.querySelector('.panel-close') as HTMLButtonElement;
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.destroy();
    });

    const header = this.el.querySelector('.panel-header') as HTMLDivElement;
    header.addEventListener('mousedown', (e) => this.onMouseDown(e));
    header.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });

    this.refresh();
  }

  private refresh(): void {
    this.plantNameEl.textContent = this.plant.species.name;

    const stage = this.plant.getCurrentStage();
    this.stageEl.textContent = stage ? `· ${stage.name}` : '';

    const state = this.plant.state;
    const color = stateColors[state] || '#4a6b3a';
    this.stateEl.style.background = color + '1a';
    this.stateEl.style.borderColor = color + '30';
    (this.stateEl.querySelector('.state-dot') as HTMLElement).style.background = color;
    this.stateLabelEl.textContent = stateNames[state] || state;

    const health = Math.round(this.plant.health);
    let healthColor: string;
    if (health > 70) healthColor = '#4a6b3a';
    else if (health > 45) healthColor = '#b89830';
    else if (health > 20) healthColor = '#b06038';
    else healthColor = '#a05050';
    this.healthFill.style.width = `${health}%`;
    this.healthFill.style.background = healthColor;
    this.healthLabel.textContent = `生命 ${health}`;

    const stress = this.plant.getStressLevels();
    const eco = this.plant.species.ecology;

    const gauges = [
      { value: this.plant.supplementalLight, optimal: eco.light.optimal, range: eco.light.range, stress: stress.light, unit: '%' },
      { value: this.plant.moisture, optimal: eco.moisture.optimal, range: eco.moisture.range, stress: stress.moisture, unit: '%' },
      { value: this.plant.temperature, optimal: eco.temperature.optimal, range: eco.temperature.range, stress: stress.temperature, unit: '°C' }
    ];

    gauges.forEach((g, i) => {
      const row = this.gaugeRows[i];
      let pct: number;
      if (g.unit === '°C') {
        pct = Math.max(0, Math.min(100, (g.value / 40) * 100));
      } else {
        pct = Math.max(0, Math.min(100, g.value));
      }

      let gaugeColor: string;
      if (g.stress < 0.2) gaugeColor = '#4a6b3a';
      else if (g.stress < 0.5) gaugeColor = '#b89830';
      else if (g.stress < 0.7) gaugeColor = '#b06038';
      else gaugeColor = '#a05050';

      let status: string;
      if (g.stress < 0.15) status = '充足';
      else if (g.stress < 0.4) status = '尚可';
      else status = '不足';

      const valueStr = g.unit === '°C' ? `${g.value.toFixed(1)}°C` : `${Math.round(g.value)}%`;

      const fill = row.querySelector('.gauge-fill') as HTMLElement;
      fill.style.width = `${pct}%`;
      fill.style.background = gaugeColor;

      const valEl = row.querySelector('.gauge-value') as HTMLElement;
      valEl.textContent = valueStr;
      valEl.style.color = gaugeColor;

      const statusEl = row.querySelector('.gauge-status') as HTMLElement;
      statusEl.textContent = status;
      statusEl.style.color = gaugeColor;
    });

    this.adviceEl.innerHTML = this.buildAdvice(stress);
  }

  private buildAdvice(stress: { light: number; moisture: number; temperature: number }): string {
    if (stress.light < 0.15 && stress.moisture < 0.15 && stress.temperature < 0.15) {
      return '植物状态良好，继续保持';
    }

    const parts: string[] = [];
    if (stress.light > 0.3) {
      parts.push('光照不足，试试增强补光');
    }
    if (stress.moisture > 0.3) {
      parts.push('水分不足，可以浇一些水');
    }
    if (stress.temperature > 0.3) {
      parts.push('温度不适，植物有些不舒服');
    }

    if (parts.length === 0) return '植物状态尚可，微调即可';
    return parts.join('<br>');
  }

  getPlant(): Plant {
    return this.plant;
  }

  getScreenRect(): DOMRect {
    return this.el.getBoundingClientRect();
  }

  destroy(): void {
    this.unsubUpdate();
    document.removeEventListener('mousemove', this.boundOnMouseMove);
    document.removeEventListener('mouseup', this.boundOnMouseUp);
    document.removeEventListener('touchmove', this.boundOnMouseMove as any);
    document.removeEventListener('touchend', this.boundOnMouseUp as any);
    if (this.el.parentNode) {
      this.el.parentNode.removeChild(this.el);
    }
    this.onDestroy();
  }

  private onMouseDown(e: MouseEvent): void {
    if ((e.target as HTMLElement).closest('.panel-close')) return;
    e.preventDefault();
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;
    this.panelStartX = this.el.offsetLeft;
    this.panelStartY = this.el.offsetTop;
    this.dragging = true;
    this.el.classList.add('dragging');
    document.addEventListener('mousemove', this.boundOnMouseMove);
    document.addEventListener('mouseup', this.boundOnMouseUp);
  }

  private onTouchStart(e: TouchEvent): void {
    const touch = e.touches[0];
    if (!touch) return;
    e.preventDefault();
    this.dragStartX = touch.clientX;
    this.dragStartY = touch.clientY;
    this.panelStartX = this.el.offsetLeft;
    this.panelStartY = this.el.offsetTop;
    this.dragging = true;
    this.el.classList.add('dragging');
    document.addEventListener('touchmove', this.boundOnMouseMove as any);
    document.addEventListener('touchend', this.boundOnMouseUp as any);
  }

  private onMouseMove(e: MouseEvent | TouchEvent): void {
    if (!this.dragging) return;
    const clientX = 'touches' in e ? (e as TouchEvent).touches[0]?.clientX ?? this.dragStartX : (e as MouseEvent).clientX;
    const clientY = 'touches' in e ? (e as TouchEvent).touches[0]?.clientY ?? this.dragStartY : (e as MouseEvent).clientY;
    const dx = clientX - this.dragStartX;
    const dy = clientY - this.dragStartY;
    this.el.style.left = `${this.panelStartX + dx}px`;
    this.el.style.top = `${this.panelStartY + dy}px`;
    this.el.style.transform = 'none';
  }

  private onMouseUp(_e: MouseEvent): void {
    this.dragging = false;
    this.el.classList.remove('dragging');
    document.removeEventListener('mousemove', this.boundOnMouseMove);
    document.removeEventListener('mouseup', this.boundOnMouseUp);
    document.removeEventListener('touchmove', this.boundOnMouseMove as any);
    document.removeEventListener('touchend', this.boundOnMouseUp as any);
  }
}
